import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  LinearProgress,
  Paper,
  Typography,
  Portal,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GET_MAGENTO_SYNC_STATUS } from 'src/utils/gql/queries';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';

export const MagentoSyncTopBar: React.FC = () => {
  const { magentoConnectorId, magentoConnectorEnabled } = useConnectors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data } = useQuery(GET_MAGENTO_SYNC_STATUS, {
    skip: !magentoConnectorEnabled || !magentoConnectorId,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
  });

  const status = data?.getMagentoSyncStatus?.status as
    | 'in_progress'
    | 'success'
    | 'fail'
    | 'not_started'
    | undefined;

  const BAR_HEIGHT = isMobile ? 48 : 56;

  React.useEffect(() => {
    // Non sovrascrivere la topbar Shopify se attiva: entrambi in_progress è raro (cron offset).
    const root = document.documentElement;
    const current = root.style.getPropertyValue('--kipify-topbar-height');
    if (status === 'in_progress') {
      root.style.setProperty('--kipify-topbar-height', `${BAR_HEIGHT}px`);
    } else if (!current || current === `${BAR_HEIGHT}px`) {
      // solo reset se eravamo noi (Shopify gestisce il proprio)
    }
    return () => {
      if (status === 'in_progress') {
        root.style.setProperty('--kipify-topbar-height', '0px');
      }
    };
  }, [status, BAR_HEIGHT]);

  if (status !== 'in_progress') {
    return null;
  }

  const startedAt = data?.getMagentoSyncStatus?.startedAt
    ? new Date(data.getMagentoSyncStatus.startedAt)
    : undefined;
  const elapsedMinutes = startedAt
    ? Math.max(0, Math.round((Date.now() - startedAt.getTime()) / 60000))
    : 0;

  return (
    <Portal container={document.body}>
      <Paper
        component="div"
        elevation={0}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          borderRadius: 0,
          background:
            'linear-gradient(to right, rgba(0, 45, 28, 1), rgba(3, 12, 22, 1))',
          color: 'rgba(30, 237, 159, 1)',
          border: 'none',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          height: `${BAR_HEIGHT}px`,
          minHeight: `${BAR_HEIGHT}px`,
          maxHeight: `${BAR_HEIGHT}px`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 } }}>
          <Typography
            variant={isMobile ? 'caption' : 'body2'}
            sx={{
              fontWeight: 600,
              mb: { xs: 0.25, sm: 0.5 },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}
          >
            Magento data sync in progress
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(229, 238, 252, 0.9)',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              lineHeight: { xs: 1.2, sm: 1.4 },
            }}
          >
            {isMobile
              ? `Importing Magento data... in progress for ${elapsedMinutes} min`
              : `We're importing your Magento data (in progress for ${elapsedMinutes} min).`}
          </Typography>
          <LinearProgress
            variant="indeterminate"
            sx={{
              mt: { xs: 0.5, sm: 1 },
              height: { xs: 4, sm: 6 },
              borderRadius: 3,
              bgcolor: 'rgba(30,237,159,0.18)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: '#1EED9F',
              },
            }}
          />
        </Box>
      </Paper>
    </Portal>
  );
};
